import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

const StepWizard = ({ steps, currentStep, onStepChange, onFinish, children }) => {
    const stepHeadingRef = useRef(null);
    const hasMountedRef = useRef(false);
    const progress = Math.round(((currentStep + 1) / steps.length) * 100);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        stepHeadingRef.current?.focus();
    }, [currentStep]);

    return (
        <div className="flex flex-col space-y-6 max-w-5xl mx-auto py-8 px-4">
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-4 text-sm text-slate-300">
                    <span>Étape {currentStep + 1} sur {steps.length}</span>
                    <span>{progress}% du parcours</span>
                </div>
                <div
                    className="h-2 overflow-hidden rounded-full bg-white/10"
                    role="progressbar"
                    aria-label="Progression dans la création du CV"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                >
                    <div className="h-full rounded-full bg-lenvia-blue transition-[width] duration-300" style={{ width: `${progress}%` }} />
                </div>
            </div>

            <nav aria-label="Étapes de création du CV" className="mb-4 overflow-x-auto pb-4">
              <ol className="flex min-w-max items-start justify-between gap-2">
                {steps.map((step, index) => (
                    <li key={step.id} className="min-w-[120px]">
                      <button
                        type="button"
                        aria-current={currentStep === index ? "step" : undefined}
                        aria-label={`${step.title}, étape ${index + 1} sur ${steps.length}${index < currentStep ? ", terminée" : ""}`}
                        className={cn(
                            "flex min-h-11 w-full flex-col items-center rounded-xl px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lenvia-blue",
                            currentStep === index
                                ? "text-lenvia-blue font-bold"
                                : index < currentStep
                                    ? "text-emerald-500/80 hover:text-emerald-400"
                                    : "text-slate-500 hover:text-slate-300"
                        )}
                        onClick={() => onStepChange(index)}
                    >
                        <div
                            className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center border-2 mb-2 font-bold transition-colors duration-300",
                                currentStep === index
                                    ? "border-lenvia-blue bg-lenvia-blue/20 text-lenvia-blue shadow-[0_0_15px_rgba(59,130,246,0.2)]"
                                    : index < currentStep
                                        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-500"
                                        : "border-white/10 bg-white/5 text-slate-400"
                            )}
                        >
                            {index < currentStep ? <Check aria-hidden="true" className="w-5 h-5" /> : index + 1}
                        </div>
                        <span className="text-xs uppercase tracking-wide">{step.title}</span>
                      </button>
                    </li>
                ))}
              </ol>
            </nav>

            <div className="glass rounded-3xl border border-white/10 overflow-hidden flex flex-col min-h-[500px]">
                <div className="p-6 border-b border-white/10 bg-white/5">
                    <h2 ref={stepHeadingRef} tabIndex={-1} className="text-xl font-heading font-bold text-white focus:outline-none">
                        {steps[currentStep].title}
                    </h2>
                    <p className="text-sm text-slate-400 mt-1">
                        {steps[currentStep].description}
                    </p>
                </div>
                <div className="p-6 flex-1">
                    {children}
                </div>
                <div className="p-6 border-t border-white/10 bg-white/5 flex justify-between items-center">
                    <Button
                        variant="outline"
                        onClick={() => onStepChange(currentStep - 1)}
                        disabled={currentStep === 0}
                        className="w-32 border border-slate-600 bg-slate-800/90 text-slate-100 hover:bg-slate-700 hover:text-white disabled:border-slate-800 disabled:bg-slate-900/50 disabled:text-slate-500 font-medium shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
                        data-testid="wizard-back"
                    >
                        Précédent
                    </Button>
                    <div className="flex gap-3">
                        {currentStep === steps.length - 1 ? (
                            <Button
                                className="w-32 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-md focus-visible:ring-2 focus-visible:ring-emerald-400"
                                data-testid="wizard-finish"
                                onClick={() => onFinish?.()}
                            >
                                Terminer
                            </Button>
                        ) : (
                            <Button
                                onClick={() => onStepChange(currentStep + 1)}
                                className="w-32 bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-md focus-visible:ring-2 focus-visible:ring-blue-400"
                                data-testid="wizard-next"
                            >
                                Suivant
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StepWizard;
